import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSettingsBundle } from '../../context/SettingsBundleContext';
import { buildAssistantBridgeStatus } from '../../utils/assistantBridgeStatus';
import {
  asRecord,
  Button,
  Card,
  extractSpreadsheetFileId,
  Input,
  ReadinessBanner,
  readText,
  SectionHeader,
  toOperationSummary,
} from './integrationsShared';

type AssistantBridgeSectionMode = 'safe' | 'advanced';

type SpreadsheetPreset = {
  presetId: string;
  label: string;
  destinationType: 'new_sheet' | 'existing_sheet';
  destinationFileId?: string;
  destinationFolderId?: string;
  spreadsheetUrl?: string | null;
  fileNameTemplate?: string | null;
};

const parsePresets = (
  value: Array<Record<string, unknown>>,
): SpreadsheetPreset[] =>
  value.flatMap((record) => {
    const presetId = readText(record.presetId);
    if (!presetId) {
      return [];
    }
    return [
      {
        presetId,
        label: readText(record.label, 'Untitled preset'),
        destinationType:
          record.destinationType === 'existing_sheet'
            ? 'existing_sheet'
            : 'new_sheet',
        destinationFileId: readText(record.destinationFileId) || undefined,
        destinationFolderId: readText(record.destinationFolderId) || undefined,
        spreadsheetUrl: readText(record.spreadsheetUrl) || null,
        fileNameTemplate: readText(record.fileNameTemplate) || null,
      },
    ];
  });

export const AssistantBridgeControlPlaneSection = ({
  mode = 'advanced',
  routePath,
}: {
  mode?: AssistantBridgeSectionMode;
  routePath?: string;
}) => {
  const bundle = useSettingsBundle();
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedRoute = routePath ?? location.pathname;
  const prefix =
    mode === 'safe'
      ? 'settings-integrations'
      : 'control-plane-assistant-bridge';
  const cardTestId =
    mode === 'safe'
      ? 'settings-integrations-bridge-card'
      : 'control-plane-assistant-bridge-card';

  const [redirectUri, setRedirectUri] = useState(() =>
    bundle.adapters.assistantSpreadsheetBridgeAdapter.buildCallbackUri({
      returnTo: resolvedRoute,
    }),
  );
  const [fileName, setFileName] = useState('Assistant Spreadsheet Export');
  const [workbookArtifactPath, setWorkbookArtifactPath] = useState('');
  const [destinationMode, setDestinationMode] = useState<
    'new_sheet' | 'existing_sheet'
  >('new_sheet');
  const [destinationFolderId, setDestinationFolderId] = useState('');
  const [destinationFileId, setDestinationFileId] = useState('');
  const [presetLabel, setPresetLabel] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [operationMessage, setOperationMessage] = useState('');
  const [operationError, setOperationError] = useState('');
  const [lastResult, setLastResult] = useState<Record<string, unknown>>({});
  const [callbackHandled, setCallbackHandled] = useState(false);

  const bridgeStatusQuery =
    bundle.hooks.useGetAssistantSpreadsheetBridgeStatusQuery({});
  const [startAuth, startAuthState] =
    bundle.hooks.useStartAssistantSpreadsheetBridgeAuthMutation();
  const [completeAuth, completeAuthState] =
    bundle.hooks.useCompleteAssistantSpreadsheetBridgeAuthMutation();
  const [exportSpreadsheet, exportSpreadsheetState] =
    bundle.hooks.useExportAssistantSpreadsheetToGoogleDriveMutation();
  const [disconnectBridge, disconnectBridgeState] =
    bundle.hooks.useDisconnectAssistantSpreadsheetBridgeMutation();
  const [savePreset, savePresetState] =
    bundle.hooks.useSaveAssistantSpreadsheetExportPresetMutation();
  const [deletePreset, deletePresetState] =
    bundle.hooks.useDeleteAssistantSpreadsheetExportPresetMutation();

  const isBusy =
    startAuthState.isLoading ||
    completeAuthState.isLoading ||
    exportSpreadsheetState.isLoading ||
    disconnectBridgeState.isLoading ||
    savePresetState.isLoading ||
    deletePresetState.isLoading;

  const bridgeStatus = useMemo(
    () => buildAssistantBridgeStatus(bridgeStatusQuery.data),
    [bridgeStatusQuery.data],
  );
  const statusRecord = asRecord(bridgeStatusQuery.data);
  const statusPayload = asRecord(statusRecord.payload);
  const expiryState = readText(statusPayload.expiryState, 'unknown');
  const updatedAt = readText(statusPayload.updatedAt, 'Not available');
  const recentSheetExports = Array.isArray(statusPayload.recentSheetExports)
    ? statusPayload.recentSheetExports.map(asRecord).slice(0, 5)
    : [];

  const bridgeBannerState =
    bridgeStatusQuery.isLoading
      ? 'loading'
      : bridgeStatus.connected
        ? bridgeStatus.supportedEnvironment
          ? 'healthy'
          : 'warning'
        : mode === 'safe' && bridgeStatus.supportedEnvironment
          ? 'healthy'
          : bridgeStatus.supportedEnvironment
            ? 'blocker'
            : 'warning';

  const bridgeBannerDescription = [
    mode === 'safe'
      ? `Service export: ${bridgeStatus.supportedEnvironment ? 'Available' : 'Unavailable'}`
      : `Connected: ${bridgeStatus.connected ? 'Yes' : 'No'}`,
    `Token State: ${expiryState}`,
    `Mode: ${bridgeStatus.bridgeState}`,
    `Updated: ${updatedAt}`,
  ]
    .join(' · ')
    .concat(
      bridgeStatus.supportedEnvironment
        ? ''
        : ' — Download-only environment; live Sheets export requires authenticated staff context.',
    );

  const presets = useMemo(
    () => parsePresets(bridgeStatus.presets),
    [bridgeStatus.presets],
  );
  const lastResultAuthorizationUrl = readText(lastResult.authorizationUrl);
  const lastResultFileUrl = readText(lastResult.fileUrl);
  const redirectUriAllowed = useMemo(
    () =>
      bundle.adapters.assistantSpreadsheetBridgeAdapter.isRedirectUriAllowed(
        redirectUri,
      ),
    [bundle.adapters.assistantSpreadsheetBridgeAdapter, redirectUri],
  );
  const serializedPayload = useMemo(() => {
    const payload = asRecord(lastResult.payload);
    if (!Object.keys(payload).length) {
      return '';
    }
    return JSON.stringify(payload, null, 2);
  }, [lastResult]);

  useEffect(() => {
    const returnContext =
      bundle.adapters.assistantSpreadsheetBridgeAdapter.getReturnContext();
    if (!returnContext) {
      return;
    }
    if (!workbookArtifactPath && returnContext.workbookArtifactPath) {
      setWorkbookArtifactPath(returnContext.workbookArtifactPath);
    }
    if (!fileName && returnContext.workbookFileName) {
      setFileName(returnContext.workbookFileName);
    }
  }, [
    bundle.adapters.assistantSpreadsheetBridgeAdapter,
    fileName,
    workbookArtifactPath,
  ]);

  const applyOperationResult = (
    result: unknown,
    label: string,
  ): Record<string, unknown> => {
    const normalized = asRecord(result);
    setLastResult(normalized);
    const ok = Boolean(normalized.ok);
    if (ok) {
      setOperationError('');
      setOperationMessage(toOperationSummary(normalized, label));
    } else {
      setOperationMessage('');
      setOperationError(toOperationSummary(normalized, label));
    }
    return normalized;
  };

  useEffect(() => {
    if (callbackHandled) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const state = params.get('state');
    const code = params.get('code');
    if (!state || !code) {
      return;
    }

    let cancelled = false;
    const finishCallback = async () => {
      setCallbackHandled(true);
      try {
        const result = await completeAuth({ state, code }).unwrap();
        if (cancelled) {
          return;
        }
        applyOperationResult(result, 'Complete Auth');
        await bridgeStatusQuery.refetch();
        navigate(resolvedRoute, { replace: true });

        const returnContext =
          bundle.adapters.assistantSpreadsheetBridgeAdapter.getReturnContext();
        if (
          returnContext &&
          returnContext.originRoute &&
          returnContext.originRoute !== resolvedRoute
        ) {
          bundle.adapters.assistantSpreadsheetBridgeAdapter.storeCompletionContext(
            returnContext,
          );
          bundle.adapters.assistantSpreadsheetBridgeAdapter.clearReturnContext();
          navigate(returnContext.originRoute, { replace: true });
        } else {
          bundle.adapters.assistantSpreadsheetBridgeAdapter.clearReturnContext();
        }
      } catch (error) {
        if (!cancelled) {
          setOperationMessage('');
          setOperationError(
            `Complete Auth failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
          );
        }
      }
    };
    void finishCallback();

    return () => {
      cancelled = true;
    };
  }, [
    bridgeStatusQuery,
    bundle.adapters.assistantSpreadsheetBridgeAdapter,
    callbackHandled,
    completeAuth,
    location.search,
    navigate,
    resolvedRoute,
  ]);

  const handleStartAuth = async () => {
    const nextRedirectUri = readText(redirectUri);
    if (!nextRedirectUri) {
      setOperationMessage('');
      setOperationError('Start Auth: redirect URI is required.');
      return;
    }
    if (
      !bundle.adapters.assistantSpreadsheetBridgeAdapter.isRedirectUriAllowed(
        nextRedirectUri,
      )
    ) {
      setOperationMessage('');
      setOperationError(
        'Start Auth failed: redirect URI is not allowlisted for the non-production assistant Workspace OAuth client.',
      );
      return;
    }
    try {
      bundle.adapters.assistantSpreadsheetBridgeAdapter.storeReturnContext({
        originRoute: `${location.pathname}${location.search}`,
        workbookArtifactPath: null,
        workbookLabel: null,
        workbookFileName: null,
        spreadsheetDownloadPath: null,
        startedAt: new Date().toISOString(),
      });
      const result = await startAuth({ redirectUri: nextRedirectUri }).unwrap();
      const normalized = applyOperationResult(result, 'Start Auth');
      const authorizationUrl = readText(normalized.authorizationUrl);
      if (authorizationUrl && typeof window !== 'undefined') {
        window.location.assign(authorizationUrl);
      }
      await bridgeStatusQuery.refetch();
    } catch (error) {
      setOperationMessage('');
      setOperationError(
        `Start Auth failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleExport = async () => {
    const nextFileName = readText(fileName);
    const nextWorkbookArtifactPath = readText(workbookArtifactPath);
    if (!nextFileName || !nextWorkbookArtifactPath) {
      setOperationMessage('');
      setOperationError(
        'Export: file name and workbook artifact path are required.',
      );
      return;
    }
    try {
      const result = await exportSpreadsheet({
        fileName: nextFileName,
        workbookArtifactPath: nextWorkbookArtifactPath,
        destinationFolderId:
          destinationMode === 'new_sheet'
            ? readText(destinationFolderId) || undefined
            : undefined,
        destinationFileId:
          destinationMode === 'existing_sheet' && readText(destinationFileId)
            ? extractSpreadsheetFileId(destinationFileId)
            : undefined,
      }).unwrap();
      const normalized = applyOperationResult(result, 'Export');
      const resolvedFileId = readText(normalized.fileId);
      if (resolvedFileId) {
        setDestinationFileId(resolvedFileId);
      }
      await bridgeStatusQuery.refetch();
    } catch (error) {
      setOperationMessage('');
      setOperationError(
        `Export failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      const result = await disconnectBridge({}).unwrap();
      applyOperationResult(result, 'Disconnect');
      await bridgeStatusQuery.refetch();
    } catch (error) {
      setOperationMessage('');
      setOperationError(
        `Disconnect failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleSavePreset = async () => {
    if (!presetLabel.trim()) {
      setOperationMessage('');
      setOperationError('Preset label is required.');
      return;
    }
    try {
      const result = await savePreset({
        presetId: selectedPresetId || undefined,
        label: presetLabel.trim(),
        destinationType: destinationMode,
        destinationFileId:
          destinationMode === 'existing_sheet' && destinationFileId
            ? extractSpreadsheetFileId(destinationFileId)
            : undefined,
        destinationFolderId:
          destinationMode === 'new_sheet'
            ? readText(destinationFolderId) || undefined
            : undefined,
        spreadsheetUrl:
          destinationMode === 'existing_sheet'
            ? readText(destinationFileId) || null
            : null,
        fileNameTemplate: readText(fileName) || null,
      }).unwrap();
      applyOperationResult(result, 'Save preset');
      setPresetLabel('');
      await bridgeStatusQuery.refetch();
    } catch (error) {
      setOperationMessage('');
      setOperationError(
        `Save preset failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedPresetId) {
      return;
    }
    try {
      const result = await deletePreset({
        presetId: selectedPresetId,
      }).unwrap();
      applyOperationResult(result, 'Delete preset');
      setSelectedPresetId('');
      await bridgeStatusQuery.refetch();
    } catch (error) {
      setOperationMessage('');
      setOperationError(
        `Delete preset failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  return (
    <Card data-testid={cardTestId}>
      <SectionHeader
        title={
          mode === 'safe'
            ? 'Google Sheets assistant export'
            : 'Assistant Bridge Control Plane'
        }
        description={
          mode === 'safe'
            ? 'Review temporary service-created Google Sheets exports without exposing support diagnostics or OAuth controls.'
            : 'Manage redirect controls, OAuth environment diagnostics, and raw assistant bridge support tooling.'
        }
        action={(
          <Button
            variant="outline"
            onClick={() => bridgeStatusQuery.refetch()}
            disabled={bridgeStatusQuery.isFetching}
            data-testid={`${prefix}-refresh-status-button`}
          >
            {bridgeStatusQuery.isFetching ? 'Refreshing...' : 'Refresh Status'}
          </Button>
        )}
      />

      <ReadinessBanner
        state={bridgeBannerState}
        title={bridgeStatus.statusLabel}
        description={bridgeBannerDescription}
        testId={`${prefix}-status-banner`}
        className="mb-5"
      />

      {mode === 'safe' ? (
        <div
          className="mb-5 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3"
          data-testid={`${prefix}-summary-card`}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
            Bridge Status
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="text-sm text-[#374151]">
              <span className="font-medium">Service export:</span>{' '}
              {bridgeStatus.supportedEnvironment ? 'Available' : 'Unavailable'}
            </div>
            <div className="text-sm text-[#374151]">
              <span className="font-medium">Token state:</span> {expiryState}
            </div>
            <div className="text-sm text-[#374151]">
              <span className="font-medium">Environment:</span>{' '}
              {bridgeStatus.supportedEnvironment ? 'Supported' : 'Download only'}
            </div>
            <div className="text-sm text-[#374151]">
              <span className="font-medium">Updated:</span> {updatedAt}
            </div>
          </div>
          <p className="mt-3 text-xs text-text-secondary">
            Temporary Google Sheets are Intellio-owned seed exports. They are deleted after 24 hours; make a copy in Google Sheets to keep one.
          </p>
          {recentSheetExports.length ? (
            <div className="mt-4 border-t border-[#E5E7EB] pt-4" data-testid={`${prefix}-recent-sheets`}>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                Temporary Google Sheets
              </p>
              <div className="mt-2 space-y-2">
                {recentSheetExports.map((entry) => (
                  <div
                    key={readText(entry.exportId) || readText(entry.fileId)}
                    className="rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-xs text-[#374151]"
                    data-testid={`${prefix}-recent-sheet-row`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold">{readText(entry.fileName, 'Temporary Sheet')}</span>
                      <span>{readText(entry.status, 'unknown')}</span>
                    </div>
                    <div className="mt-1 text-text-secondary">
                      Expires: {readText(entry.expiresAt, 'Not available')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <p className="mt-3 text-xs text-text-secondary">
            Staff receive reader access to service-owned temporary Sheets.
            User-owned copies made in Google Sheets are outside Intellio
            tracking and cleanup.
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-3">
          {mode === 'advanced' ? (
            <>
              <Input
                label="Redirect URI"
                value={redirectUri}
                onChange={(event) => setRedirectUri(event.currentTarget.value)}
                data-testid={`${prefix}-redirect-uri-input`}
              />
              {!redirectUriAllowed ? (
                <p className="text-xs text-amber-700">
                  This redirect URI is not registered for the non-production
                  assistant Workspace OAuth client.
                </p>
              ) : null}
            </>
          ) : null}
          <Button
            variant="primary"
            onClick={handleStartAuth}
            disabled={
              isBusy || !bridgeStatus.supportedEnvironment || !redirectUriAllowed
            }
            data-testid={`${prefix}-start-auth-button`}
          >
            {startAuthState.isLoading ? 'Starting...' : 'Start Auth'}
          </Button>
          <p className="text-xs text-text-secondary">
            OAuth callback completes through the shared `/auth` route and
            returns here automatically.
          </p>

          <details className="rounded-lg border border-border overflow-hidden bg-[#F9FAFB]">
            <summary className="flex cursor-pointer list-none select-none items-center justify-between px-4 py-3 marker:hidden [&::-webkit-details-marker]:hidden hover:bg-white/60 transition-colors">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-jungle">
                Destination presets
              </p>
              <span className="text-xs text-text-tertiary">
                {presets.length} preset{presets.length !== 1 ? 's' : ''}
              </span>
            </summary>
            <div className="border-t border-border px-4 pb-4 pt-3 space-y-3">
              <select
                value={selectedPresetId}
                onChange={(event) => {
                  const nextPresetId = event.currentTarget.value;
                  setSelectedPresetId(nextPresetId);
                  const preset = presets.find(
                    (entry) => entry.presetId === nextPresetId,
                  );
                  if (!preset) {
                    return;
                  }
                  setDestinationMode(preset.destinationType);
                  setDestinationFileId(
                    preset.spreadsheetUrl ?? preset.destinationFileId ?? '',
                  );
                  setDestinationFolderId(preset.destinationFolderId ?? '');
                  if (preset.fileNameTemplate) {
                    setFileName(preset.fileNameTemplate);
                  }
                }}
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-irongrey focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">No preset selected</option>
                {presets.map((preset) => (
                  <option key={preset.presetId} value={preset.presetId}>
                    {preset.label}
                  </option>
                ))}
              </select>
              <Input
                label="Preset Label"
                value={presetLabel}
                onChange={(event) => setPresetLabel(event.currentTarget.value)}
                data-testid={`${prefix}-preset-label-input`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => void handleSavePreset()}
                  disabled={isBusy || !bridgeStatus.supportedEnvironment}
                  data-testid={`${prefix}-save-preset-button`}
                >
                  {savePresetState.isLoading ? 'Saving...' : 'Save Preset'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => void handleDeletePreset()}
                  disabled={isBusy || !selectedPresetId}
                  data-testid={`${prefix}-delete-preset-button`}
                >
                  {deletePresetState.isLoading ? 'Deleting...' : 'Delete Preset'}
                </Button>
              </div>
            </div>
          </details>
        </div>

        <div className="space-y-3">
          <Input
            label="Export File Name"
            value={fileName}
            onChange={(event) => setFileName(event.currentTarget.value)}
            data-testid={`${prefix}-file-name-input`}
          />
          <Input
            label="Workbook Artifact Path"
            value={workbookArtifactPath}
            onChange={(event) =>
              setWorkbookArtifactPath(event.currentTarget.value)
            }
            data-testid={`${prefix}-workbook-path-input`}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant={destinationMode === 'new_sheet' ? 'primary' : 'outline'}
              onClick={() => setDestinationMode('new_sheet')}
            >
              Create New Sheet
            </Button>
            <Button
              variant={
                destinationMode === 'existing_sheet' ? 'primary' : 'outline'
              }
              onClick={() => setDestinationMode('existing_sheet')}
            >
              Update Existing
            </Button>
          </div>
          {destinationMode === 'existing_sheet' ? (
            <Input
              label="Google Sheet URL or File ID"
              value={destinationFileId}
              onChange={(event) => setDestinationFileId(event.currentTarget.value)}
              data-testid={`${prefix}-destination-file-input`}
            />
          ) : (
            <Input
              label="Destination Folder ID (optional)"
              value={destinationFolderId}
              onChange={(event) =>
                setDestinationFolderId(event.currentTarget.value)
              }
              data-testid={`${prefix}-destination-folder-input`}
            />
          )}
          <p
            className="text-xs text-text-secondary"
            data-testid={`${prefix}-destination-file-help`}
          >
            New sheet creates a spreadsheet in Drive. Existing sheet refreshes
            matching tabs and preserves unrelated tabs.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              onClick={() => void handleExport()}
              disabled={isBusy || !bridgeStatus.connected}
              data-testid={`${prefix}-export-button`}
            >
              {exportSpreadsheetState.isLoading
                ? 'Exporting...'
                : 'Export Spreadsheet'}
            </Button>
            <Button
              variant="outline"
              onClick={() => void handleDisconnect()}
              disabled={isBusy || !bridgeStatus.connected}
              data-testid={`${prefix}-disconnect-button`}
            >
              {disconnectBridgeState.isLoading
                ? 'Disconnecting...'
                : 'Disconnect'}
            </Button>
          </div>
        </div>
      </div>

      {mode === 'advanced' && operationMessage ? (
        <p
          className="mt-4 text-sm font-semibold text-[#237062]"
          data-testid={`${prefix}-operation-message`}
        >
          {operationMessage}
        </p>
      ) : null}
      {mode === 'advanced' && operationError ? (
        <p
          className="mt-4 text-sm font-semibold text-[#B91C1C]"
          data-testid={`${prefix}-operation-error`}
        >
          {operationError}
        </p>
      ) : null}

      {mode === 'advanced' && lastResultAuthorizationUrl ? (
        <p
          className="mt-3 text-xs text-text-secondary"
          data-testid={`${prefix}-authorization-url`}
        >
          Authorization URL:{' '}
          <a
            href={lastResultAuthorizationUrl}
            target="_blank"
            rel="noreferrer"
            className="break-all underline text-[#1D4ED8]"
          >
            {lastResultAuthorizationUrl}
          </a>
        </p>
      ) : null}
      {mode === 'advanced' && lastResultFileUrl ? (
        <p
          className="mt-2 text-xs text-text-secondary"
          data-testid={`${prefix}-file-url`}
        >
          Export File URL:{' '}
          <a
            href={lastResultFileUrl}
            target="_blank"
            rel="noreferrer"
            className="break-all underline text-[#1D4ED8]"
          >
            {lastResultFileUrl}
          </a>
        </p>
      ) : null}
      {mode === 'advanced' && serializedPayload ? (
        <pre
          className="mt-3 overflow-auto rounded-md border border-[#E5E7EB] bg-[#F3F4F6] p-3 text-xs text-[#111827]"
          data-testid={`${prefix}-operation-payload`}
        >
          {serializedPayload}
        </pre>
      ) : null}
    </Card>
  );
};
